import { flexPos } from '@/assets/styles/mixin';
import { color } from '@/assets/styles/var';
import { FC, ForwardedRef, ReactNode, forwardRef } from 'react';
import { styled } from 'styled-components';
import { TipsWrap } from '@/assets/styles/common';
import { $fontSize, $height, $width } from '@/assets/styles/mediaSize';
import { robotName, query } from '@/utils/const';
import Image from '../MediaComp/Image';
import useUserStore from '@/store/userStore';
import { callFromExternal } from '@/utils';

const UserMessageWrap = styled.div<{ $isMe?: boolean }>`
  &:not(:last-child) {
    margin-bottom: 22rem;
  }

  .user-message-content {
    ${(props) =>
      props.$isMe ? flexPos('flex-end', 'flex-start') : flexPos('flex-start', 'flex-start')}

    .name {
      ${$fontSize('24rem', '14rem')}
      color: ${color.gray};
      margin-bottom: 16rem;
      text-align: ${(props) => (props.$isMe ? 'right' : 'left')};
    }

    .avatar-wrap {
      ${$width('84rem', '36rem')}
      ${$height('84rem', '36rem')}
      border-radius: 50%;

      > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }
  }
`;

interface IUserMessage {
  children: ReactNode;
  defaultAvatar: string;
  name: string;
  isMe?: boolean;
  time?: string;
  className?: string;
  senderFaceURL?: string;
}

const UserMessage = forwardRef((props: IUserMessage, ref: ForwardedRef<any>) => {
  const msgRef = useRef<any>();
  const userStore = useUserStore();

  useImperativeHandle<any, any>(ref, () => ({
    msgRef: msgRef.current,
  }));

  const nickName = useMemo(() => {
    if (props.isMe) {
      let _userName = '';
      if (query?.nickName) {
        _userName = query.nickName;
      } else {
        if (query?.nickId) {
          _userName = `${$t('chat_session_status_16')}-${query.nickId}`;
        } else {
          _userName = `${$t('chat_session_status_15')}-${userStore.sessionConfig.nickId}`;
        }
      }
      return _userName;
    }

    if (props.name !== undefined) {
      return props.name;
    }

    return $t(robotName);
  }, [props.name]);

  // user avatar
  const avatar = useMemo(() => {
    if (props.isMe) {
      return query.headIcon || props.defaultAvatar;
    }
    if (props.senderFaceURL) {
      let senderAvatar;
      try {
        senderAvatar = JSON.parse(props.senderFaceURL);
        if (!senderAvatar?.url) {
          senderAvatar = props.defaultAvatar;
        }
      } catch (error) {
        senderAvatar = props.defaultAvatar;
      }
      return senderAvatar;
    }
    return props.defaultAvatar;
  }, [props.senderFaceURL]);
  const external = callFromExternal()
  return (
    <UserMessageWrap className={props.className} $isMe={props.isMe} ref={msgRef}>
      {!!props.time && <TipsWrap>{props.time}</TipsWrap>}

      <div className="user-message-content">
        <div className={props.isMe ? 'order-1' : 'order-2'}>
          <div className={`name ${ external ? `external-nickname` : ''}`}>{nickName}</div>
          {props.children}
        </div>

        <div
          className={`avatar-wrap ${props.isMe ? 'order-2' : 'order-1'} ${
            props.isMe ? 'ml-16' : 'mr-16'
          } ${ external ? `external-avatar` : '' }`}
        >
          <Image src={avatar} />
        </div>
      </div>
    </UserMessageWrap>
  );
});

export default UserMessage;
